import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import apiService from '../services/api';
import { 
  Send,
  Calendar, 
  Clock, 
  BookOpen, 
  CheckCircle, 
  XCircle,
  Play,
  MessageCircle,
  ExternalLink,
  Edit,
  AlertCircle,
  Filter,
  Search,
  X,
  FileText,
  Download,
  Trash2,
  Plus
} from 'lucide-react';

interface LearningSession {
  id: number;
  teacher: {
    id: number;
    username: string;
    fullName: string;
    profilePhotoUrl: string;
  };
  learner: {
    id: number;
    username: string;
    fullName: string;
    profilePhotoUrl: string;
  };
  skill: {
    id: number;
    name: string;
    category: {
      id: number;
      name: string;
    };
  };
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELED' | 'NO_SHOW';
  scheduledStartTime: string;
  scheduledEndTime: string;
  actualStartTime?: string;
  actualEndTime?: string;
  meetingLink?: string;
  meetingNotes?: string;
  credits: number;
  creditProcessed: boolean;
  materials?: SessionMaterial[];
  createdAt: string;
  updatedAt: string;
}

interface SessionRequest {
  id: number;
  requester: {
    id: number;
    username: string;
    fullName: string;
  };
  recipient: {
    id: number;
    username: string;
    fullName: string;
  };
  skill: {
    id: number;
    name: string;
  };
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'CANCELED';
  proposedDateTime: string;
  durationMinutes: number;
  message: string;
  meetingLink?: string;
  createdAt: string;
}

interface SessionMaterial {
  id: number;
  fileName: string;
  fileUrl: string;
  // fileSize and fileType are not required in UI anymore
  fileSize: number;
  fileType: string;
  description: string;
  uploadedBy: {
    id: number;
    username: string;
    fullName: string;
  };
  createdAt: string;
}

const Sessions: React.FC = () => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [sessions, setSessions] = useState<LearningSession[]>([]);
  const [sessionRequests, setSessionRequests] = useState<SessionRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'sessions' | 'requests' | 'materials'>('sessions');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSessionModal, setShowSessionModal] = useState<LearningSession | null>(null);
  const [showRequestModal, setShowRequestModal] = useState<SessionRequest | null>(null);
  const [showMeetingLinkModal, setShowMeetingLinkModal] = useState<LearningSession | null>(null);
  const [materials, setMaterials] = useState<SessionMaterial[]>([]);
  const [selectedSessionForMaterials, setSelectedSessionForMaterials] = useState<LearningSession | null>(null);
  const [showUploadMaterialModal, setShowUploadMaterialModal] = useState(false);
  const [newMaterial, setNewMaterial] = useState({
    fileName: '',
    description: '',
    fileUrl: ''
  });
  const [isProcessingRequest, setIsProcessingRequest] = useState<number | null>(null);

  // Chat state
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedSessionForChat, setSelectedSessionForChat] = useState<LearningSession | null>(null);
  const [chatRoom, setChatRoom] = useState<any>(null);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  // WebSocket connection for real-time chat
  useEffect(() => {
    if (!showChatModal || !chatRoom) return;

    // Check if WebSocket is supported
    if (!('WebSocket' in window)) {
      console.warn('WebSocket is not supported in this browser');
      return;
    }

    // For now, we'll disable WebSocket connections until the backend is properly configured
    // This prevents the error messages you're seeing
    console.log('Chat WebSocket connection disabled - backend WebSocket service not yet configured');
    return;

    // TODO: Implement proper STOMP WebSocket connection when backend is ready
    // const wsUrl = `ws://localhost:8080/ws`;
    // const websocket = new WebSocket(wsUrl);
    // ... STOMP implementation
  }, [showChatModal, chatRoom]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (showChatModal && chatMessages.length > 0) {
      const chatContainer = document.querySelector('.chat-messages-container');
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    }
  }, [chatMessages, showChatModal]);

  // Check for new session requests and show notifications
  useEffect(() => {
    if (sessionRequests.length > 0) {
      const newRequests = sessionRequests.filter(r => r.status === 'PENDING');
      if (newRequests.length > 0) {
        // Show notification for new pending requests
        newRequests.forEach(request => {
          if (request.recipient.id === user?.id) {
            addNotification({
              type: 'info',
              title: 'New Session Request',
              message: `You have a new session request for "${request.skill.name}"`,
              category: 'session'
            });
          }
        });
      }
    }
  }, [sessionRequests, user, addNotification]);

    const fetchData = async () => {
    try {
      setIsLoading(true);
      const [sessionsResponse, requestsResponse] = await Promise.all([
        apiService.getSessions(),
        apiService.getSessionRequests()
      ]);
      
      const sessionsWithMaterials = await Promise.all(
        (sessionsResponse.data || []).map(async (session: LearningSession) => {
          try {
            const materialsResponse = await apiService.getSessionMaterialsBySession(session.id);
            return {
              ...session,
              materials: materialsResponse.data || []
            };
          } catch (error) {
            console.error(`Failed to fetch materials for session ${session.id}:`, error);
            return {
              ...session,
              materials: []
            };
          }
        })
      );
      
      setSessions(sessionsWithMaterials);
      setSessionRequests(requestsResponse.data || []);
    } catch (error: any) {
      toast.error('Failed to load sessions data');
      console.error('Error fetching sessions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMaterials = async (sessionId?: number) => {
    try {
      let response;
      if (sessionId) {
        response = await apiService.getSessionMaterialsBySession(sessionId);
      } else {
        response = await apiService.getSessionMaterials();
      }
      setMaterials(response.data || []);
    } catch (error: any) {
      toast.error('Failed to load session materials');
      console.error('Error fetching materials:', error);
    }
  };

  const filteredSessions = sessions.filter(session => {
    const matchesSearch = session.skill.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         session.teacher.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         session.learner.fullName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || session.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredRequests = sessionRequests.filter(request => {
    const matchesSearch = request.skill.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         request.requester.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         request.recipient.fullName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleUpdateSessionStatus = async (sessionId: number, status: string) => {
    try {
      // Check if user is the teacher for this session
      const session = sessions.find(s => s.id === sessionId);
      if (session && !isUserTeacher(session)) {
        toast.error('Only the teacher can update session status');
        return;
      }

      // If starting a session, show meeting link modal first
      if (status === 'IN_PROGRESS') {
        if (session) {
          setShowMeetingLinkModal(session);
          return;
        }
      }
      
      await apiService.updateSessionStatus(sessionId, status);
      setSessions(prev => prev.map(session => 
        session.id === sessionId ? { ...session, status: status as any } : session
      ));
      
      // Add notification for session status change
      if (session) {
        addNotification({
          type: 'success',
          title: 'Session Updated',
          message: `Session "${session.skill.name}" status changed to ${status.replace('_', ' ')}`,
          category: 'session'
        });
      }
      
      toast.success('Session status updated successfully!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update session status');
    }
  };

  const handleStartSessionWithMeetingLink = async (sessionId: number, meetingLink: string) => {
    try {
      // Update session status to IN_PROGRESS and add meeting link
      await apiService.updateSessionMeetingDetails(sessionId, meetingLink);
      await apiService.updateSessionStatus(sessionId, 'IN_PROGRESS');
      
      // Refresh sessions data
      await fetchData();
      setShowMeetingLinkModal(null);
      toast.success('Session started successfully with meeting link!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to start session');
    }
  };

  const handleUploadMaterial = async () => {
    if (!newMaterial.fileName || !newMaterial.fileUrl) {
      toast.error('File name and URL are required');
      return;
    }

    try {
      const materialData = {
        ...newMaterial,
        sessionId: selectedSessionForMaterials?.id
      };

      await apiService.createSessionMaterial(materialData);
      setShowUploadMaterialModal(false);
      setNewMaterial({
        fileName: '',
        description: '',
        fileUrl: ''
      });
      
      // Refresh the sessions data to show the new material
      await fetchData();
      
      toast.success('Material uploaded successfully!');
    } catch (error: any) {
      toast.error('Failed to upload material');
      console.error('Error uploading material:', error);
    }
  };

  const handleDeleteMaterial = async (materialId: number) => {
    if (!window.confirm('Are you sure you want to delete this material?')) {
      return;
    }

    try {
      await apiService.deleteSessionMaterial(materialId);
      
      // Refresh the sessions data to reflect the deletion
      await fetchData();
      
      toast.success('Material deleted successfully!');
    } catch (error: any) {
      toast.error('Failed to delete material');
      console.error('Error deleting material:', error);
    }
  };

  // Chat functions
  const openChat = async (session: LearningSession) => {
    try {
      setIsLoadingChat(true);
      setSelectedSessionForChat(session);
      
      // Determine the other user (the one to chat with)
      const otherUserId = session.teacher.id === user?.id ? session.learner.id : session.teacher.id;
      
      // Get or create chat room
      const chatRoomResponse = await apiService.getOrCreateChatRoom(otherUserId, session.skill.id);
      setChatRoom(chatRoomResponse.data);
      
      // Load chat messages
      const messagesResponse = await apiService.getChatMessages(chatRoomResponse.data.id);
      setChatMessages(messagesResponse.data);
      
      setShowChatModal(true);
    } catch (error: any) {
      toast.error('Failed to open chat');
      console.error('Error opening chat:', error);
    } finally {
      setIsLoadingChat(false);
    }
  };

  const sendChatMessage = async () => {
    if (!newMessage.trim() || !chatRoom) return;

    try {
      setIsSendingMessage(true);
      
      // Determine the recipient (the other user in the session)
      const recipientId = selectedSessionForChat?.teacher.id === user?.id 
        ? selectedSessionForChat?.learner.id 
        : selectedSessionForChat?.teacher.id;

      const messageData = {
        chatRoomId: chatRoom.id,
        recipientId: recipientId,
        content: newMessage.trim()
      };

      const response = await apiService.sendMessage(messageData);
      
      // Add the new message to the chat
      setChatMessages(prev => [...prev, response.data]);
      setNewMessage('');
      
      // Mark messages as read
      await apiService.markMessagesAsRead(chatRoom.id);
      
    } catch (error: any) {
      toast.error('Failed to send message');
      console.error('Error sending message:', error);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const closeChat = () => {
    setShowChatModal(false);
    setSelectedSessionForChat(null);
    setChatRoom(null);
    setChatMessages([]);
    setNewMessage('');
  };

  // Removed file size formatter since we are no longer showing file size
  const formatFileSize = (_bytes: number) => '';

  const handleAcceptRequest = async (requestId: number) => {
    try {
      setIsProcessingRequest(requestId);
      await apiService.acceptSessionRequest(requestId);
      setSessionRequests(prev => prev.map(request => 
        request.id === requestId ? { ...request, status: 'ACCEPTED' } : request
      ));
      
      // Add notification for accepted request
      const request = sessionRequests.find(r => r.id === requestId);
      if (request) {
        addNotification({
          type: 'success',
          title: 'Session Request Accepted',
          message: `Session request for "${request.skill.name}" has been accepted`,
          category: 'session'
        });
      }
      
      // Refresh sessions and navigate to Sessions tab so teacher can add meeting link
      await fetchData();
      setActiveTab('sessions');
      toast.success('Session request accepted!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to accept request');
    } finally {
      setIsProcessingRequest(null);
    }
  };

  const handleRejectRequest = async (requestId: number) => {
    try {
      setIsProcessingRequest(requestId);
      await apiService.rejectSessionRequest(requestId);
      setSessionRequests(prev => prev.map(request => 
        request.id === requestId ? { ...request, status: 'REJECTED' } : request
      ));
      
      // Add notification for rejected request
      const request = sessionRequests.find(r => r.id === requestId);
      if (request) {
        addNotification({
          type: 'warning',
          title: 'Session Request Rejected',
          message: `Session request for "${request.skill.name}" has been rejected`,
          category: 'session'
        });
      }
      
      toast.success('Session request rejected');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject request');
    } finally {
      setIsProcessingRequest(null);
    }
  };

  const handleCancelRequest = async (requestId: number) => {
    try {
      setIsProcessingRequest(requestId);
      await apiService.cancelSessionRequest(requestId);
      setSessionRequests(prev => prev.map(request => 
        request.id === requestId ? { ...request, status: 'CANCELED' } : request
      ));
      
      // Add notification for canceled request
      const request = sessionRequests.find(r => r.id === requestId);
      if (request) {
        addNotification({
          type: 'info',
          title: 'Session Request Canceled',
          message: `Session request for "${request.skill.name}" has been canceled`,
          category: 'session'
        });
      }
      
      toast.success('Session request canceled');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to cancel request');
    } finally {
      setIsProcessingRequest(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SCHEDULED': return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'CANCELED': return 'bg-red-100 text-red-800';
      case 'NO_SHOW': return 'bg-gray-100 text-gray-800';
      case 'PENDING': return 'bg-orange-100 text-orange-800';
      case 'ACCEPTED': return 'bg-green-100 text-green-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SCHEDULED': return <Calendar className="h-4 w-4" />;
      case 'IN_PROGRESS': return <Play className="h-4 w-4" />;
      case 'COMPLETED': return <CheckCircle className="h-4 w-4" />;
      case 'CANCELED': return <XCircle className="h-4 w-4" />;
      case 'NO_SHOW': return <AlertCircle className="h-4 w-4" />;
      case 'PENDING': return <Clock className="h-4 w-4" />;
      case 'ACCEPTED': return <CheckCircle className="h-4 w-4" />;
      case 'REJECTED': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString();
  };

  const isUserTeacher = (session: LearningSession) => session.teacher.id === user?.id;
  const isUserLearner = (session: LearningSession) => session.learner.id === user?.id;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4 pulse-glow"></div>
          <h2 className="text-xl font-bold gradient-text">Loading Sessions...</h2>
          <p className="text-gray-600">Preparing your learning schedule</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold gradient-text">Learning Sessions</h1>
            <p className="text-gray-600 mt-2 text-lg">Manage your scheduled and completed sessions</p>
          </div>
          <div className="flex items-center space-x-6">
            <div className="text-right">
              <p className="text-sm text-gray-500">Total Sessions</p>
              <p className="text-2xl font-bold text-blue-600">{sessions.length}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Pending Requests</p>
              <p className="text-2xl font-bold text-orange-600">
                {sessionRequests.filter(r => r.status === 'PENDING').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6 hover:shadow-xl transition-all duration-300">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type="text"
                placeholder="Search sessions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-300 focus:scale-[1.02]"
              />
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-300"
            >
              <option value="all">All Status</option>
              <option value="SCHEDULED">Scheduled</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELED">Canceled</option>
              <option value="NO_SHOW">No Show</option>
              <option value="PENDING">Pending</option>
              <option value="ACCEPTED">Accepted</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-lg border border-blue-100">
        <div className="border-b border-blue-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('sessions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-all duration-300 ${
                activeTab === 'sessions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-300'
              }`}
            >
              Sessions ({filteredSessions.length})
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-all duration-300 ${
                activeTab === 'requests'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-300'
              }`}
            >
              Session Requests ({filteredRequests.length})
            </button>
            <button
              onClick={() => {
                setActiveTab('materials');
                fetchMaterials();
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-all duration-300 ${
                activeTab === 'materials'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-300'
              }`}
            >
              Materials ({materials.length})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'sessions' ? (
            <div className="space-y-4">
              {/* Information banner for sessions */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-bold">🎓</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-green-800 mb-1">Learning Session Actions</h3>
                    <div className="text-sm text-green-700 space-y-1">
                      <p>• <strong>Teachers:</strong> Can start and complete sessions, update meeting details</p>
                      <p>• <strong>Learners:</strong> Can view session progress and join meetings when started</p>
                      <p>• <strong>Session Control:</strong> Only teachers have the authority to manage session status</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {filteredSessions.map((session) => (
                <div key={session.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <BookOpen className="h-5 w-5 text-blue-600" />
                        <h3 className="font-medium text-gray-900">{session.skill.name}</h3>
                      </div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                        {getStatusIcon(session.status)}
                        <span className="ml-1">{session.status.replace('_', ' ')}</span>
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowSessionModal(session)}
                        className="p-2 text-gray-400 hover:text-blue-600"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Role and Action Information */}
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {isUserTeacher(session) ? (
                          <>
                            <div className="h-4 w-4 rounded-full bg-green-500 flex items-center justify-center">
                              <span className="text-white text-xs font-bold">T</span>
                            </div>
                            <span className="text-sm text-blue-800 font-medium">You are the Teacher</span>
                            <span className="text-xs text-blue-600">• You can start and complete sessions</span>
                          </>
                        ) : (
                          <>
                            <div className="h-4 w-4 rounded-full bg-blue-500 flex items-center justify-center">
                              <span className="text-white text-xs font-bold">L</span>
                            </div>
                            <span className="text-sm text-blue-800 font-medium">You are the Learner</span>
                            <span className="text-xs text-blue-600">• Waiting for teacher to start the session</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Participants</h4>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <div className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center">
                            {session.teacher.profilePhotoUrl ? (
                              <img
                                src={session.teacher.profilePhotoUrl}
                                alt={session.teacher.fullName || session.teacher.username}
                                className="h-8 w-8 rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-white text-sm font-medium">
                                {session.teacher.fullName?.charAt(0) || session.teacher.username.charAt(0)}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {session.teacher.fullName || session.teacher.username}
                            </p>
                            <p className="text-xs text-gray-500">Teacher</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                            {session.learner.profilePhotoUrl ? (
                              <img
                                src={session.learner.profilePhotoUrl}
                                alt={session.learner.fullName || session.learner.username}
                                className="h-8 w-8 rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-white text-sm font-medium">
                                {session.learner.fullName?.charAt(0) || session.learner.username.charAt(0)}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {session.learner.fullName || session.learner.username}
                            </p>
                            <p className="text-xs text-gray-500">Learner</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Schedule</h4>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-600">
                          <Clock className="inline h-4 w-4 mr-1" />
                          {formatDateTime(session.scheduledStartTime)}
                        </p>
                        <p className="text-sm text-gray-600">
                          Duration: {Math.round((new Date(session.scheduledEndTime).getTime() - new Date(session.scheduledStartTime).getTime()) / 60000)} minutes
                        </p>
                        <p className="text-sm text-gray-600">
                          Credits: {session.credits}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Session Materials Section */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-700">Session Materials</h4>
                      <button
                        onClick={() => {
                          setSelectedSessionForMaterials(session);
                          setShowUploadMaterialModal(true);
                        }}
                        className="flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Material
                      </button>
                    </div>
                    
                    {/* Materials List for this session */}
                    <div className="space-y-2">
                      {session.materials && session.materials.length > 0 ? (
                        <>
                          {/* Show first 2 materials */}
                          {session.materials.slice(0, 2).map((material: any) => (
                            <div key={material.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                              <div className="flex items-center space-x-2">
                                <FileText className="h-3 w-3 text-blue-600" />
                                <span className="text-xs text-gray-700">{material.fileName}</span>
                                {material.description && (
                                  <span className="text-xs text-gray-500">- {material.description}</span>
                                )}
                              </div>
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => window.open(material.fileUrl, '_blank')}
                                  className="p-1 text-gray-400 hover:text-blue-600"
                                  title="Download"
                                >
                                  <Download className="h-3 w-3" />
                                </button>
                                {material.uploadedBy?.id === user?.id && (
                                  <button
                                    onClick={() => handleDeleteMaterial(material.id)}
                                    className="p-1 text-gray-400 hover:text-red-600"
                                    title="Delete"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                          
                          {/* Show "View All" button if there are more than 2 materials */}
                          {session.materials.length > 2 && (
                            <button
                              onClick={() => {
                                setSelectedSessionForMaterials(session);
                                setActiveTab('materials');
                              }}
                              className="w-full text-xs text-blue-600 hover:text-blue-800 py-1 hover:bg-blue-50 rounded transition-colors"
                            >
                              View all {session.materials.length} materials →
                            </button>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-gray-500 italic">No materials uploaded yet</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-4">
                      {session.meetingLink && (
                        <a
                          href={session.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-blue-600 hover:text-blue-700"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          <span className="text-sm">Join Meeting</span>
                        </a>
                      )}
                      <button
                        onClick={() => openChat(session)}
                        className="flex items-center text-gray-600 hover:text-gray-700 hover:text-blue-600 transition-colors"
                      >
                        <MessageCircle className="h-4 w-4 mr-1" />
                        <span className="text-sm">Message</span>
                      </button>
                    </div>

                    <div className="flex items-center space-x-2">
                      {isUserTeacher(session) && session.status === 'SCHEDULED' && (
                        <button
                          onClick={() => handleUpdateSessionStatus(session.id, 'IN_PROGRESS')}
                          className="flex items-center px-3 py-1 text-sm bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                        >
                          <Play className="h-4 w-4 mr-1" />
                          Start Session
                        </button>
                      )}
                      {isUserTeacher(session) && session.status === 'IN_PROGRESS' && (
                        <button
                          onClick={() => handleUpdateSessionStatus(session.id, 'COMPLETED')}
                          className="flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Complete Session
                        </button>
                      )}
                      {isUserLearner(session) && session.status === 'SCHEDULED' && (
                        <div className="text-xs text-gray-500 italic">
                          Waiting for teacher to start session
                        </div>
                      )}
                      {isUserLearner(session) && session.status === 'IN_PROGRESS' && (
                        <div className="text-xs text-gray-500 italic">
                          Session in progress
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {filteredSessions.length === 0 && (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No sessions found</p>
                </div>
              )}
            </div>
          ) : activeTab === 'materials' ? (
            <div className="space-y-4">
              {/* Materials Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Session Materials</h2>
                  <p className="text-sm text-gray-600">Upload and manage materials for your learning sessions</p>
                </div>
                <button
                  onClick={() => setShowUploadMaterialModal(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Upload Material
                </button>
              </div>

              {/* Session Filter for Materials */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-4">
                  <label className="text-sm font-medium text-gray-700">Filter by Session:</label>
                  <select
                    value={selectedSessionForMaterials?.id || ''}
                    onChange={(e) => {
                      const sessionId = e.target.value ? parseInt(e.target.value) : null;
                      const session = sessionId ? sessions.find(s => s.id === sessionId) : null;
                      setSelectedSessionForMaterials(session || null);
                      if (sessionId) {
                        fetchMaterials(sessionId);
                      } else {
                        fetchMaterials();
                      }
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Sessions</option>
                    {sessions.map(session => (
                      <option key={session.id} value={session.id}>
                        {session.skill.name} - {formatDateTime(session.scheduledStartTime)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Materials List */}
              {materials.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No materials found</p>
                  <p className="text-gray-400">Upload materials to get started</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {materials.map((material) => (
                    <div key={material.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-5 w-5 text-blue-600" />
                          <div>
                            <h3 className="font-medium text-gray-900 text-sm">{material.fileName}</h3>
                            <p className="text-xs text-gray-500">{material.fileType || 'Unknown type'}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => window.open(material.fileUrl, '_blank')}
                            className="p-1 text-gray-400 hover:text-blue-600"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                          {material.uploadedBy.id === user?.id && (
                            <button
                              onClick={() => handleDeleteMaterial(material.id)}
                              className="p-1 text-gray-400 hover:text-red-600"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {material.description && (
                        <p className="text-sm text-gray-600 mb-3">{material.description}</p>
                      )}

                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{formatFileSize(material.fileSize)}</span>
                        <span>Uploaded by {material.uploadedBy.fullName || material.uploadedBy.username}</span>
                      </div>

                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-xs text-gray-400">
                          {formatDateTime(material.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Information banner for session requests */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-bold">ℹ️</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-blue-800 mb-1">Session Request Actions</h3>
                    <div className="text-sm text-blue-700 space-y-1">
                      <p>• <strong>If you requested:</strong> You can only cancel the request</p>
                      <p>• <strong>If you received:</strong> You can accept, reject, or cancel the request</p>
                      <p>• <strong>Pending requests</strong> show available actions based on your role</p>
                      <p>• <strong>Important:</strong> Only teachers can start and complete learning sessions</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {filteredRequests.map((request) => (
                <div key={request.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <BookOpen className="h-5 w-5 text-blue-600" />
                        <h3 className="font-medium text-gray-900">{request.skill.name}</h3>
                      </div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                        {getStatusIcon(request.status)}
                        <span className="ml-1">{request.status}</span>
                      </span>
                      {/* Show user's role in this request */}
                      {user && (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          request.requester.id === user.id 
                            ? 'bg-orange-100 text-orange-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {request.requester.id === user.id ? 'You requested' : 'You received'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowRequestModal(request)}
                        className="p-2 text-gray-400 hover:text-blue-600"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Participants</h4>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                            request.requester.id === user?.id ? 'bg-green-500' : 'bg-orange-500'
                          }`}>
                            <span className="text-white text-sm font-medium">
                              {request.requester.fullName?.charAt(0) || request.requester.username.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {request.requester.fullName || request.requester.username}
                              {request.requester.id === user?.id && ' (You)'}
                            </p>
                            <p className="text-xs text-gray-500">Requester</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                            request.recipient.id === user?.id ? 'bg-green-500' : 'bg-blue-500'
                          }`}>
                            <span className="text-white text-sm font-medium">
                              {request.recipient.fullName?.charAt(0) || request.recipient.username.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {request.recipient.fullName || request.recipient.username}
                              {request.recipient.id === user?.id && ' (You)'}
                            </p>
                            <p className="text-xs text-gray-500">Recipient</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Details</h4>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-600">
                          <Clock className="inline h-4 w-4 mr-1" />
                          {formatDateTime(request.proposedDateTime)}
                        </p>
                        <p className="text-sm text-gray-600">
                          Duration: {request.durationMinutes} minutes
                        </p>
                        <p className="text-sm text-gray-600">
                          Created: {formatDateTime(request.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {request.message && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Message</h4>
                      <p className="text-sm text-gray-600">{request.message}</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => {
                          // For session requests, we can open chat with the other user
                          const otherUserId = request.requester.id === user?.id ? request.recipient.id : request.requester.id;
                          // Create a temporary session object for chat
                          const tempSession = {
                            id: 0,
                            teacher: {
                              ...request.recipient,
                              profilePhotoUrl: ''
                            },
                            learner: {
                              ...request.requester,
                              profilePhotoUrl: ''
                            },
                            skill: {
                              ...request.skill,
                              category: { id: 0, name: 'General' } // Default category
                            },
                            status: 'SCHEDULED' as const,
                            scheduledStartTime: request.proposedDateTime,
                            scheduledEndTime: new Date(new Date(request.proposedDateTime).getTime() + (request.durationMinutes * 60000)).toISOString(),
                            credits: 0,
                            creditProcessed: false,
                            createdAt: request.createdAt,
                            updatedAt: request.createdAt
                          };
                          openChat(tempSession);
                        }}
                        className="flex items-center text-gray-600 hover:text-gray-700 hover:text-blue-600 transition-colors"
                      >
                        <MessageCircle className="h-4 w-4 mr-1" />
                        <span className="text-sm">Message</span>
                      </button>
                    </div>

                    <div className="flex items-center space-x-2">
                      {/* Show Accept/Reject buttons only for the recipient */}
                      {request.status === 'PENDING' && request.recipient.id === user?.id && (
                        <>
                          <button
                            onClick={() => handleAcceptRequest(request.id)}
                            disabled={isProcessingRequest === request.id}
                            className={`flex items-center px-3 py-1 text-sm rounded-md transition-colors ${
                              isProcessingRequest === request.id
                                ? 'bg-green-400 text-white cursor-not-allowed'
                                : 'bg-green-600 text-white hover:bg-green-700'
                            }`}
                          >
                            {isProcessingRequest === request.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 mr-1 border-2 border-white border-t-transparent"></div>
                                Accepting...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Accept
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleRejectRequest(request.id)}
                            disabled={isProcessingRequest === request.id}
                            className={`flex items-center px-3 py-1 text-sm rounded-md transition-colors ${
                              isProcessingRequest === request.id
                                ? 'bg-red-400 text-white cursor-not-allowed'
                                : 'bg-red-600 text-white hover:bg-red-700'
                            }`}
                          >
                            {isProcessingRequest === request.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 mr-1 border-2 border-white border-t-transparent"></div>
                                Rejecting...
                              </>
                            ) : (
                              <>
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </>
                            )}
                          </button>
                        </>
                      )}
                      {/* Show Cancel button only for the requester */}
                      {request.status === 'PENDING' && request.requester.id === user?.id && (
                        <button
                          onClick={() => handleCancelRequest(request.id)}
                          disabled={isProcessingRequest === request.id}
                          className={`flex items-center px-3 py-1 text-sm rounded-md transition-colors ${
                            isProcessingRequest === request.id
                              ? 'bg-gray-400 text-white cursor-not-allowed'
                              : 'bg-gray-600 text-white hover:bg-gray-700'
                          }`}
                        >
                          {isProcessingRequest === request.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 mr-1 border-2 border-white border-t-transparent"></div>
                              Canceling...
                            </>
                          ) : (
                            <>
                              <X className="h-4 w-4 mr-1" />
                              Cancel
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {filteredRequests.length === 0 && (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No session requests found</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Session Details Modal */}
      {showSessionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Session Details</h2>
              <button
                onClick={() => setShowSessionModal(null)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-900">{showSessionModal.skill.name}</h3>
                <p className="text-sm text-gray-600">{showSessionModal.skill.category.name}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Teacher</h4>
                  <p className="text-sm text-gray-900">
                    {showSessionModal.teacher.fullName || showSessionModal.teacher.username}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Learner</h4>
                  <p className="text-sm text-gray-900">
                    {showSessionModal.learner.fullName || showSessionModal.learner.username}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Start Time</h4>
                  <p className="text-sm text-gray-900">
                    {formatDateTime(showSessionModal.scheduledStartTime)}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">End Time</h4>
                  <p className="text-sm text-gray-900">
                    {formatDateTime(showSessionModal.scheduledEndTime)}
                  </p>
                </div>
              </div>

              {showSessionModal.meetingLink && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Meeting Link</h4>
                  <a
                    href={showSessionModal.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    {showSessionModal.meetingLink}
                  </a>
                </div>
              )}

              {showSessionModal.meetingNotes && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Notes</h4>
                  <p className="text-sm text-gray-900">{showSessionModal.meetingNotes}</p>
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(showSessionModal.status)}`}>
                    {getStatusIcon(showSessionModal.status)}
                    <span className="ml-1">{showSessionModal.status.replace('_', ' ')}</span>
                  </span>
                  <span className="text-sm text-gray-600">
                    Credits: {showSessionModal.credits}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Request Details Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Request Details</h2>
              <button
                onClick={() => setShowRequestModal(null)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-900">{showRequestModal.skill.name}</h3>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Requester</h4>
                  <p className="text-sm text-gray-900">
                    {showRequestModal.requester.fullName || showRequestModal.requester.username}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Recipient</h4>
                  <p className="text-sm text-gray-900">
                    {showRequestModal.recipient.fullName || showRequestModal.recipient.username}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Proposed Time</h4>
                  <p className="text-sm text-gray-900">
                    {formatDateTime(showRequestModal.proposedDateTime)}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Duration</h4>
                  <p className="text-sm text-gray-900">
                    {showRequestModal.durationMinutes} minutes
                  </p>
                </div>
              </div>

              {showRequestModal.message && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Message</h4>
                  <p className="text-sm text-gray-900">{showRequestModal.message}</p>
                </div>
              )}

              {showRequestModal.meetingLink && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Meeting Link</h4>
                  <a
                    href={showRequestModal.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    {showRequestModal.meetingLink}
                  </a>
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(showRequestModal.status)}`}>
                  {getStatusIcon(showRequestModal.status)}
                  <span className="ml-1">{showRequestModal.status}</span>
                </span>
                <span className="text-sm text-gray-600">
                  Created: {formatDateTime(showRequestModal.createdAt)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Meeting Link Modal */}
      {showMeetingLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Start Session</h2>
              <button
                onClick={() => setShowMeetingLinkModal(null)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Session Details</h3>
                <p className="text-sm text-gray-600">
                  <strong>Skill:</strong> {showMeetingLinkModal.skill.name}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Learner:</strong> {showMeetingLinkModal.learner.fullName || showMeetingLinkModal.learner.username}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Time:</strong> {formatDateTime(showMeetingLinkModal.scheduledStartTime)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meeting Link (Required)
                </label>
                <input
                  type="url"
                  id="meetingLink"
                  placeholder="https://meet.google.com/... or https://zoom.us/..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowMeetingLinkModal(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const meetingLink = (document.getElementById('meetingLink') as HTMLInputElement)?.value;
                    if (!meetingLink) {
                      toast.error('Please enter a meeting link');
                      return;
                    }
                    handleStartSessionWithMeetingLink(showMeetingLinkModal.id, meetingLink);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
                >
                  Start Session
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full Screen Loading Overlay for Session Requests */}
      {isProcessingRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Processing Request</h3>
            <p className="text-gray-600">Please wait while we process your session request...</p>
          </div>
        </div>
      )}

      {/* Upload Material Modal */}
      {showUploadMaterialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Upload Material</h2>
              <button
                onClick={() => setShowUploadMaterialModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {selectedSessionForMaterials && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Session:</strong> {selectedSessionForMaterials.skill.name}
                </p>
                <p className="text-sm text-blue-700">
                  <strong>Time:</strong> {formatDateTime(selectedSessionForMaterials.scheduledStartTime)}
                </p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File Name *
                </label>
                <input
                  type="text"
                  value={newMaterial.fileName}
                  onChange={(e) => setNewMaterial(prev => ({ ...prev, fileName: e.target.value }))}
                  placeholder="Enter file name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File URL *
                </label>
                <input
                  type="url"
                  value={newMaterial.fileUrl}
                  onChange={(e) => setNewMaterial(prev => ({ ...prev, fileUrl: e.target.value }))}
                  placeholder="https://example.com/file.pdf"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Removed File Type and File Size inputs per request */}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={newMaterial.description}
                  onChange={(e) => setNewMaterial(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description of the material"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadMaterialModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUploadMaterial}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
                >
                  Upload Material
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      {showChatModal && selectedSessionForChat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4 h-[80vh] flex flex-col">
            {/* Chat Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Chat - {selectedSessionForChat.skill.name}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {selectedSessionForChat.teacher.id === user?.id ? 'Teaching' : 'Learning'} session
                  </p>
                </div>
              </div>
              <button
                onClick={closeChat}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 chat-messages-container">
              {isLoadingChat ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
                </div>
              ) : chatMessages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <MessageCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                chatMessages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.sender.id === user?.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        message.sender.id === user?.id
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-xs font-medium">
                          {message.sender.username}
                        </span>
                        <span className="text-xs opacity-75">
                          {new Date(message.sentAt).toLocaleTimeString([], { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </span>
                      </div>
                      <p className="text-sm">{message.content}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Chat Input */}
            <div className="border-t border-gray-200 p-4">
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                  placeholder="Type your message..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isSendingMessage}
                />
                <button
                  onClick={sendChatMessage}
                  disabled={!newMessage.trim() || isSendingMessage}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isSendingMessage ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Send</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sessions; 